import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useCustomDispatch } from '../../hooks'
import { useAuthSelector } from '../../services/selectors/authSelector'
import { useLocation } from 'react-router-dom'

import { ValidateReturnedPatches } from '../../services/apis/dispense/validateReturnPatches'
import { ErrorMessage } from './AllConfirmationMessages'

import { UploadTripilicatesPatientSigns } from './uploadTripilicatesPatientSigns'
import { GenrateRxLink } from '../../services/apis/dispense/genrateRxLink'
import { LoadingView } from './loader'
import { useAllSelector } from '../../services/selectors/allSelector'
import { NewRxesConsultationDropdown } from './deliveryConsultationDropdown'
import { isRxFullyDelivered } from '../../utils/helper/deliveredRxDisplay'

export const DispenseRxData = ({ rxData,
	newRxCounsellingValues,
	setNewRxCounsellingValues,
	type = '',
	patientData,
	selectedDoses,
	setSelectedDoses,
	hideFlexi = true,
	selectedDeliverRxIds = [],
	setSelectedDeliverRxIds = () => { },
	isActiveDeliverRxCard = true,
	deliverRxSelectionEnabled = true,
	// Delivered history rows: display only, never selected or submitted.
	readOnly = false,
}) => {

	const [image, setImage] = useState('')
	const location = useLocation()?.pathname

	const allowedPages = ['/e-sign',
		'/flexi-delivery',
		'/driver-today-pending',
		'/add-vitals',
		'/driver-route-today',
		'/driver-today-missed-route',
		'/driver-missed-route'];

	const isAllowedPage = allowedPages.includes(location);
	const showDeliverRxSelection = isAllowedPage;

	// RX enable/disable: derive from patientData when available (same as driver app), else use prop. When delivery_status === 'delivered', RX checkboxes are disabled.
	const deliverRxEnabled = patientData != null
		? (patientData?.delivery_status !== 'delivered')
		: deliverRxSelectionEnabled;

	// When dropdown is opened (this card is active), set selectedDeliverRxIds: all RX ids, excluding only when dose data exists and every dose has can_deliver === false
	const rxDataKey = rxData?.length ? rxData.map((r) => r.id).join(',') : ''
	useEffect(() => {
		if (readOnly || !showDeliverRxSelection || !isActiveDeliverRxCard || !rxDataKey || !setSelectedDeliverRxIds) return
		const allIds = rxDataKey.split(',').filter(Boolean).map((id) => Number(id))
		// Include: (1) already delivered RXs so they show checked and disabled with delivered dose(s), (2) otherwise exclude only when dose data exists and every dose has can_deliver === false
		const deliverableIds = rxData?.length
			? allIds.filter((id) => {
				const r = rxData.find((rx) => rx.id === id)
				if (!r) return true
				if (r?.rx_access?.can_deliver === false) return false
				if (String(r?.delivery_status || '').toLowerCase() === 'delivered') return true
				const doses = r?.investigate_dilaudid_delivery_all || []
				if (doses.length === 0) return true
				return !doses.every((d) => d?.can_deliver === false)
			})
			: allIds
		setSelectedDeliverRxIds(deliverableIds)
	}, [readOnly, showDeliverRxSelection, isActiveDeliverRxCard, rxDataKey, setSelectedDeliverRxIds, rxData])

	// When any RX has all dilaudid doses can_deliver === false, remove that parent id from selection so it stays unchecked
	useEffect(() => {
		if (readOnly || !showDeliverRxSelection || !setSelectedDeliverRxIds || !rxData?.length) return
		const idsToUncheck = rxData.filter((r) => {
			if (String(r?.delivery_status || '').toLowerCase() === 'delivered') return false
			const doses = r?.investigate_dilaudid_delivery_all || []
			return doses.length > 0 && doses.every((d) => d?.can_deliver === false)
		}).map((r) => r.id)
		if (idsToUncheck.length === 0) return
		setSelectedDeliverRxIds((prev) => {
			if (!prev?.length) return prev
			const next = prev.filter((id) => !idsToUncheck.includes(id))
			return next.length === prev.length ? prev : next
		})
	}, [readOnly, showDeliverRxSelection, setSelectedDeliverRxIds, rxData])

	// useEffect(() => {
	// 	console.log('selectedDeliverRxIds', selectedDeliverRxIds)
	// }, [selectedDeliverRxIds])

	return (
		<React.Fragment>
			{rxData
				?.slice()
				?.sort((a, b) => {
					if (a?.is_din_matched?.status === 1 && b?.is_din_matched?.status !== 1) {
						return -1;
					} else if (a?.is_din_matched?.status !== 1 && b?.is_din_matched?.status === 1) {
						return 1;
					} else {
						return 0;
					}
				})
				?.map((value, index) => {
					const dilaudid_delivery = value?.investigate_dilaudid_delivery_history || value?.investigate_dilaudid_delivery_all
					const isEmergency = value?.rxsig ? value?.rxsig.toLowerCase().includes('emergency') : false;
					const isFlexiDelivery = value?.flexi_delivery
					const staleStatusColor = value?.flexi_stale_status === 'orange' ? '#ff4d01' : value?.flexi_stale_status === 'red' ? 'red' : '#31C48D'
					const isFlexiDeliveryDate = value?.flexi_date
					const flexiType = value?.flexi_type
					const isRxDelivered = String(value?.delivery_status || '').toLowerCase() === 'delivered'
					const rxCanDeliver = value?.rx_access?.can_deliver !== false
					const assignedOwnerName = value?.assigned_rx_owner?.name
					const assignedOwnerType = value?.assigned_rx_owner?.type
					// can_deliver logic only when status is not delivered: only when ALL doses have can_deliver === false → row disabled
					const allDosesCanDeliverFalse = !readOnly && !isRxDelivered && dilaudid_delivery?.length > 0 &&
						dilaudid_delivery.every((d) => d?.can_deliver === false)
					const singleDoseDeliveredTime = getSingleDoseDeliveredTime(value, dilaudid_delivery)
					// Delivered Rxs (read-only rows, single dose delivered, or backend roll-up) need no "Assigned to" banner.
					const isFullyDelivered = readOnly || isRxFullyDelivered(value) ||
						(isRxDelivered && !(dilaudid_delivery?.length > 0)) ||
						(dilaudid_delivery?.length > 0 && dilaudid_delivery.every((d) => String(d?.delivery_status || '').toLowerCase() === 'delivered'))
					const isParentChecked = showDeliverRxSelection && selectedDeliverRxIds?.includes(value?.id)
					const isCheckboxDisabled = showDeliverRxSelection && (!deliverRxEnabled || !rxCanDeliver || allDosesCanDeliverFalse)

					const handleDeliverRxToggle = () => {
						if (readOnly) return
						if (value?.id == null || !setSelectedDeliverRxIds || !deliverRxEnabled || !rxCanDeliver) return
						if (allDosesCanDeliverFalse) return // Condition 3: do not allow selecting when all doses explicitly can_deliver === false
						const willBeChecked = !selectedDeliverRxIds?.includes(value.id)
						setSelectedDeliverRxIds(prev => {
							const next = prev ? [...prev] : []
							const idx = next.indexOf(value.id)
							if (idx === -1) next.push(value.id)
							else next.splice(idx, 1)
							return next
						})
						if (willBeChecked && dilaudid_delivery?.length > 0) {
							// Show API state: select all doses where can_deliver === true; rest stay unselected (read-only)
							const fromApi = dilaudid_delivery
								.filter((d) => d?.can_deliver === true)
								.map((d) => ({
									frequency: d?.frequency,
									dose_id: d?.id,
									dose_no: d?.dose_no,
									dose_gap_prompt: d?.dose_gap_prompt
								}))
							setSelectedDoses(prev => ({ ...prev, [value.rxnumber]: fromApi }))
						} else if (!willBeChecked) {
							setSelectedDoses(prev => ({ ...prev, [value.rxnumber]: [] }))
						}
					}

					return (
						<React.Fragment key={index}>
							<div className={`mt-2 mb-2 break-all ${allDosesCanDeliverFalse ? 'opacity-60' : ''}`}>
								<div className="p-2 rounded-md"
									style={{ backgroundColor: value?.is_din_matched?.status === 1 && value?.is_din_matched?.color, border: '1px solid lightgray' }}>

									<div className='flex gap-2 justify-between items-center flex-wrap'>
										<div className='flex gap-2 items-center'>
											{showDeliverRxSelection && readOnly && (
												<span
													title="Already delivered"
													className="flex items-center justify-center shrink-0 w-7 h-7 rounded border border-green-200 bg-green-50 text-green-600 cursor-not-allowed"
												>
													<svg className="w-3 h-3 pointer-events-none" fill="currentColor" viewBox="0 0 20 20">
														<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
													</svg>
												</span>
											)}
											{showDeliverRxSelection && !readOnly && (
												<label
													className={`flex items-center justify-center shrink-0 w-7 h-7 rounded border transition-all duration-200 shadow-sm ${isCheckboxDisabled ? 'opacity-50 bg-gray-100 text-gray-500 cursor-not-allowed border-gray-100' : isParentChecked ? 'cursor-pointer border-blue-600 bg-blue-600 hover:bg-blue-700 text-white' : 'cursor-pointer border-gray-100 bg-white hover:border-blue-400 hover:bg-blue-50/50'}`}
													onClick={(e) => {
														if (isCheckboxDisabled) {
															e.preventDefault();
															return;
														}
														e.preventDefault();
														handleDeliverRxToggle();
													}}
												>
													<input
														type="checkbox"
														checked={!!isParentChecked}
														onChange={(e) => { e.stopPropagation(); handleDeliverRxToggle(); }}
														disabled={isCheckboxDisabled}
														className="sr-only"
														tabIndex={-1}
													/>
													{isParentChecked && (
														<svg className="w-3 h-3 pointer-events-none" fill="currentColor" viewBox="0 0 20 20">
															<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
														</svg>
													)}
												</label>
											)}
											<p className="text-sm font-normal text-white rounded-md p-1 " style={{ fontSize: '12px', height: '29px', background: 'black', paddingLeft: '10px', paddingRight: '10px' }}>
												{value?.rxnumber}
											</p>

											{value.rx_witness === 'Y' &&
												<p className="text-sm font-normal text-white bg-yellow-500 rounded-md p-1 " style={{ fontSize: '12px', height: '29px', background: '#6A057B', paddingLeft: '10px', paddingRight: '10px' }}>
													Witness Required
												</p>}

											<GenrateRxNumberDataPDF value={value} />

											{!readOnly && value?.is_triplicates_pending?.is_triplicates &&
												<div className='text-sm font-normal text-white bg-red-500 rounded-md' onClick={() => setImage(value?.is_triplicates_pending?.precription_image)}>
													<UploadTripilicatesPatientSigns
														index={index}
														rxData={value}
														image={image} />
												</div>
										}
									</div>

									<div className='flex gap-1 flex-wrap items-center ml-auto'>
									{
											isFlexiDelivery &&
											<div className='flex gap-1 flex-wrap'>
												{hideFlexi &&
													<div style={{ height: '25px' }} className={`text-xs font-normal bg-yellow-500 text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														FLEXI
													</div>}

												{
													!hideFlexi && isFlexiDeliveryDate &&
													<div style={{ height: '25px' }} className={`text-xs font-normal bg-yellow-500 text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														{isFlexiDeliveryDate}
													</div>
												}

												{flexiType && (
													<div style={{ height: '25px' }} className={`text-xs font-normal ${flexiType === 'delivery' ? 'bg-blue-500' : 'bg-purple-500'} text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														{flexiType.toUpperCase()}
													</div>
												)}

												<div style={{ height: '25px', background: staleStatusColor }} className={`text-xs font-normal text-white  px-4 py-1 flex items-center h-fit justify-center rounded-full`}>
													STALE
												</div>
											</div>
										}
										{singleDoseDeliveredTime && <RxDeliveredBadge time={singleDoseDeliveredTime} />}
									</div>
									</div>

									<h3 className="text-sm font-medium my-1">
										[{value.refill_reqty}] {value.drug}
									</h3>

									<p className="text-sm text-gray-900">{value?.rxsig}</p>
									{(value?.return_handover_details?.returned_by || value?.return_handover_details?.accepted_by || value?.return_handover_details?.accepted_at) && (
										<p className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-600">
											Returned by {value.return_handover_details.returned_by || 'the driver'}
											{value.return_handover_details.accepted_by ? ` | Accepted by ${value.return_handover_details.accepted_by}` : ''}
											{value.return_handover_details.accepted_at ? ` | ${value.return_handover_details.accepted_at}` : ''}
										</p>
									)}

									<p className='pt-1' dangerouslySetInnerHTML={{ __html: value.prescriptionExpiryDay }} />
									{value.note && <p className=' text-gray-700 bg-gray-200 p-1 pl-2 rounded-md font-medium text-sm italic' style={{ color: 'black'}}>{value?.note}</p>}

									{!readOnly && patientData?.delivery_status !== 'delivered' && value?.is_din_matched?.is_fentanyl === 1
										&& patientData?.patients_win_rx_data?.length > 0 &&
										<div className={showDeliverRxSelection && patientData && !selectedDeliverRxIds?.includes(value?.id) ? 'opacity-50 pointer-events-none' : ''}>
											<ReturnedPatches
												index={index}
												rxData={value}
												disabled={showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)}
											/>
										</div>}
									{/* <InvestageRxPDF value={value} /> */}

									{type === 'pending_counselling_rxes' &&
										<div>
											{value?.drug_counselling_sheet &&
												<div className='mt-2 border border-gray-400 rounded-md p-2 text-sm h-80  bg-white overflow-y-auto pending-counselling-sheet'
													dangerouslySetInnerHTML={{ __html: value?.drug_counselling_sheet }}
												>
												</div>
											}
											<div className='mt-2'>
												<NewRxesConsultationDropdown
													type={type}
													counsellingValues={newRxCounsellingValues}
													rxData={value}
													setCounsellingValues={setNewRxCounsellingValues}
													disabled={showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)} />
											</div>
										</div>
									}

									{/* An Rx already delivered needs no consultation, even while
									    the patient is still pending on another Rx. */}
									{ (readOnly || isFullyDelivered || value?.counsling_provided === 1) ? null :
										value?.is_new_rx && type === 'new_rx' && !isEmergency && (patientData ? patientData?.delivery_status !== 'delivered' : value?.delivery_status !== 'delivered') &&
										<div className='mt-2'>
											<NewRxesConsultationDropdown
												type={type}
												counsellingValues={newRxCounsellingValues}
												rxData={value}
												setCounsellingValues={setNewRxCounsellingValues}
												disabled={showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)}
											/>
										</div>
									}

									<DoseFrequencySelector
										dilaudidDelivery={dilaudid_delivery}
										selectedDoses={selectedDoses?.[value?.rxnumber] || []}
										setSelectedDoses={(doses) => {
											if (readOnly) return
											setSelectedDoses((prev) => ({
												...prev,
												[value?.rxnumber]: doses,
											}))
										}}
										previous_delivery_time_diff={value?.previous_delivery_time_diff}
										minutes_until_can_deliver={readOnly ? null : value?.minutes_until_can_deliver}
										disabled={!readOnly && showDeliverRxSelection && !selectedDeliverRxIds?.includes(value?.id)}
										readOnly={readOnly}
									/>

									{!isFullyDelivered && assignedOwnerType === 'driver' && assignedOwnerName && (
										<p className="mt-2 w-full min-w-0 rounded-md bg-blue-700 px-2 py-2 text-[11px] font-medium leading-tight text-white whitespace-normal" style={{ wordBreak: 'normal', overflowWrap: 'anywhere' }}>
											Assigned to {assignedOwnerName}
										</p>
									)}
								</div>
							</div>
						</React.Fragment>
					)
				})
			}
		</React.Fragment >
	)
}


export const GenrateRxNumberDataPDF = ({ value }) => {

	const { pharmacyId } = useAuthSelector()
	const { GenrateRxLinkResponse } = useAllSelector()
	const dispatch = useCustomDispatch()

	const handleGenrateRxLink = (value) => {
		const requestedData = {
			pharmacy_id: pharmacyId,
			rxnum: value.rxnumber,
			rx_br: value.rx_br
		}
		dispatch(GenrateRxLink(requestedData))
	}

	return (
		<React.Fragment>
			{value?.rx_br &&
				<p className="p-1" onClick={() => handleGenrateRxLink(value)}>
					{/* <img src={medicalHistory} alt='' className='w-6 h-6 rounded-sm' /> */}
					<svg xmlns="http://www.w3.org/2000/svg" className='w-6 h-6' viewBox="0 0 64 64" width="512" height="512"><g id="File"><path d="M18,3V24H8V5a2.006,2.006,0,0,1,2-2Z" style={{ fill: '#bcbec0' }} /><path d="M25,3V25H14V5a2.006,2.006,0,0,1,2-2Z" style={{ fill: '#d1d3d4' }} /><path d="M56,5V25H20V5a2.006,2.006,0,0,1,2-2H54A2.006,2.006,0,0,1,56,5Z" style={{ fill: '#e6e7e8' }} /><path d="M61,27V57a4,4,0,0,1-4,4H7a4,4,0,0,1-4-4V19a2.006,2.006,0,0,1,2-2H24.22a1,1,0,0,1,.97.76l1.62,6.48a1,1,0,0,0,.97.76H59A2.006,2.006,0,0,1,61,27Z" style={{ fill: '#ffa733' }} /><circle cx="32" cy="43" r="10" style={{ fill: '#ffd422' }} /><polygon points="38 41 38 45 34 45 34 49 30 49 30 45 26 45 26 41 30 41 30 37 34 37 34 41 38 41" style={{ fill: '#ff5023' }} /><polygon points="52 3 52 11 49 9 46 11 46 3 52 3" style={{ fill: '#ff5023' }} /><path d="M41.707,13.707l-1.414-1.414L38,14.586l-.826-.826A3,3,0,0,0,36,8H32a1,1,0,0,0-1,1v8h2V14h1.586l2,2-2.293,2.293,1.414,1.414L38,17.414l2.293,2.293,1.414-1.414L39.414,16ZM36,12H33V10h3a1,1,0,0,1,0,2Z" style={{ fill: '#1e81ce' }} /></g></svg>
				</p>}
			{GenrateRxLinkResponse?.loading &&
				<div className="fixed top-0 left-0 w-full h-full bg-gray-800 opacity-20 flex justify-center items-center z-50">
					<LoadingView />
				</div>}

		</React.Fragment>
	)
}

// Investage Rx PDF – prescription signature status / "Prescription signed on" footer (same as Driver)
export const InvestageRxPDF = ({ value }) => {
	const dispatch = useCustomDispatch();
	const { pharmacyId } = useAuthSelector();
	const { GenrateRxLinkResponse } = useAllSelector();
	const [showToast, setShowToast] = useState(false);
	const [loading, setLoading] = useState(true);

	const handleGenrateRxLink = (val) => {
		const requestedData = {
			pharmacy_id: pharmacyId,
			rxnum: val.rxnumber,
			rx_br: val.rx_br
		};
		setLoading(true);
		dispatch(GenrateRxLink(requestedData));
	};

	const pharmacyWinrxRxEs = value?.pharmacyWinrxRxEs ?? value?.pharmacy_winrx_rx_es;

	return (
		<React.Fragment>
			{pharmacyWinrxRxEs ?
				(pharmacyWinrxRxEs?.signature_required === 1 && pharmacyWinrxRxEs?.signature_complete === 0 ? (
					<div className="mt-1 rounded-md investageRx whitespace-nowrap text-white pl-2 p-1" style={{ fontSize: '12px' }}>
						Patient signature required for this <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => handleGenrateRxLink(value)}>prescription</span>
					</div>
				) : pharmacyWinrxRxEs?.signature_complete === 1 && (
					<p className="mt-1 rounded-md investageRx whitespace-nowrap text-white pl-2 p-1" style={{ fontSize: '12px' }}>
						Prescription signed on {pharmacyWinrxRxEs?.signature_date}
					</p>
				))
				: null
			}
			{GenrateRxLinkResponse?.loading && loading &&
				<div className="fixed top-0 left-0 w-full h-full bg-gray-800 opacity-20 flex justify-center items-center z-50">
					<LoadingView />
				</div>}
			{showToast &&
				<div className="fixed top-0 left-0 w-full h-full flex justify-center items-center z-50">
					<ErrorMessage message={'Oops, we got an issue when loading the PDF. Please try again later.'} setShowToast={() => setShowToast(false)} />
				</div>}
		</React.Fragment>
	);
};

export const CounsellingNewRxData = ({ rxData,
	counsellingOption,
	handleRadioChange,
	patient_name
}) => {

	const radioOptions = [
		{ value: 'Yes', label: <><strong>Yes,</strong> Counselling Provided</> },
		{ value: 'No', label: <><strong>No,</strong> Counselling not Provided</> },
		{ value: 'Refused', label: <>Patient <strong>Refused</strong> Counselling</> }
	];

	return (
		<React.Fragment>
			{rxData
				?.slice()
				?.sort((a, b) => {
					if (a?.is_din_matched?.status === 1 && b?.is_din_matched?.status !== 1) {
						return -1;
					} else if (a?.is_din_matched?.status !== 1 && b?.is_din_matched?.status === 1) {
						return 1;
					} else {
						return 0;
					}
				})
				?.map((value, index) => (
					<React.Fragment key={index}>
						<div className="mt-2 mb-2 break-all">
							<div className="p-2 rounded-md" style={{ backgroundColor: value?.is_din_matched?.status === 1 && value?.is_din_matched?.color, border: '1px solid lightgray' }}>
								<span className="text-sm font-normal text-white bg-green-500 rounded-md p-1" style={{ fontSize: '12px', height: '29px', background: 'black', paddingLeft: '10px', paddingRight: '10px' }}>
									{value?.rxnumber}
								</span>
								<h3 className="text-sm font-medium my-1">
									[{value?.refill_reqty}]{value?.drug}
								</h3>
								<p className="text-sm text-gray-900">{value?.rxsig}</p>
								<p className="pt-1" dangerouslySetInnerHTML={{ __html: value.prescriptionExpiryDay }} />
								{value.note && <p className=' text-gray-700 bg-gray-200 p-1 pl-2 rounded-md' style={{ color: 'black', fontSize: '10px' }}>{value?.note}</p>}
							</div>
						</div>

						{radioOptions?.map((option, idx) => (
							<div key={idx} className="text-sm mt-1 bg-gray-50 rounded-md px-3 py-2 flex items-center gap-2 uppercase w-full">
								<input
									type="radio"
									name={`counselling-${index}`}
									value={option.value}
									checked={counsellingOption[index] === option.value}
									onChange={(e) => handleRadioChange(e, index, value, patient_name)}
									className="outline-none"
									id={`${option.value.toLowerCase()}-${index}`}
									style={{ outline: 'none', border: '0.1px solid gray', borderRadius: '4px' }}
								/>
								<label htmlFor={`${option.value.toLowerCase()}-${index}`}>{option.label}</label>
							</div>
						))}

					</React.Fragment>
				))}
		</React.Fragment>
	)
}


export const PendingPharmacistSignRxData = ({ rxData,
	selectedDoses,
	setSelectedDoses
}) => {

	return (
		<React.Fragment>
			{rxData
				?.slice()
				?.sort((a, b) => {
					if (a?.is_din_matched?.status === 1 && b?.is_din_matched?.status !== 1) {
						return -1;
					} else if (a?.is_din_matched?.status !== 1 && b?.is_din_matched?.status === 1) {
						return 1;
					} else {
						return 0;
					}
				})?.map((value, index) => {
					const isFlexiDelivery = value?.flexi_delivery
					const staleStatusColor = value?.flexi_stale_status === 'orange' ? '#ff4d01' : value?.flexi_stale_status === 'red' ? 'red' : '#31C48D'
					const flexiType = value?.flexi_type
					return (
						<React.Fragment key={index}>
							{value?.is_din_matched?.status === 0 ? null :
								<div className="mt-2 mb-2 break-all">
									<div className="p-2 rounded-md" style={{ backgroundColor: value?.is_din_matched?.status === 1 && value?.is_din_matched?.color, border: '1px solid lightgray' }}>

										<div className='flex gap-2 justify-between items-center'>
											<div className='flex'>
												<span className="text-sm font-normal text-white bg-green-500 rounded-md p-1 " style={{ fontSize: '12px', height: '29px', background: 'black', paddingLeft: '10px', paddingRight: '10px' }}>{value?.rxnumber}</span>

												{/* <span className='text-xs  bg-gray-50 rounded-md px-3 py-1 flex items-center gap-2 uppercase ml-2'>
											<input type='checkbox' className='outline-none' id={`signatures-${index}`} style={{ outline: 'none', border: '0.1px solid gray', borderRadius: '4px', }} />
											<label htmlFor={`signatures-${index}`}>
												Ignore
											</label>
										</span> */}
											</div>

											{
												isFlexiDelivery &&
												<div className='flex gap-1'>
													<div style={{ height: '25px' }} className={`text-xs font-normal bg-yellow-500 text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
														FLEXI
													</div>
													{flexiType && (
														<div style={{ height: '25px' }} className={`text-xs font-normal ${flexiType === 'delivery' ? 'bg-blue-500' : 'bg-purple-500'} text-white  px-3 py-1 flex items-center h-fit justify-center rounded-full`}>
															{flexiType === 'delivery' ? 'DELIVERY' : 'PICKUP'}
														</div>
													)}
													<div style={{ height: '25px', background: staleStatusColor }} className={`text-xs font-normal text-white  px-4 py-1 flex items-center h-fit justify-center rounded-full`}>
														STALE
													</div>
												</div>
											}
										</div>

										<h3 className="text-sm font-medium my-1">[{value?.refill_reqty}]{value?.drug}</h3>
										<p className="text-sm text-gray-900">{value?.rxsig}</p>
										<p className='pt-1' dangerouslySetInnerHTML={{ __html: value?.prescriptionExpiryDay }} />

										{value.note && <p className=' text-gray-700 bg-gray-200 p-1 pl-2 rounded-md' style={{ color: 'black', fontSize: '10px' }}>{value?.note}</p>}

									<DoseFrequencySelector
										dilaudidDelivery={value?.investigate_dilaudid_delivery_all}
										selectedDoses={selectedDoses[value?.rxnumber] || []}
										setSelectedDoses={(doses) =>
											setSelectedDoses((prev) => ({
												...prev,
												[value?.rxnumber]: doses,
											}))
										}
										previous_delivery_time_diff={value?.previous_delivery_time_diff}
										minutes_until_can_deliver={value?.minutes_until_can_deliver}
									/>

									</div>
								</div>}
						</React.Fragment>
					)
				})}
		</React.Fragment>
	)
}

export const ReturnedPatches = ({ index, rxData, disabled = false }) => {

	const [selectedValue, setSelectedValue] = useState(null);
	const dispatch = useCustomDispatch()
	const [showToast, setShowToast] = useState({ successToast: false, errorToast: false })
	const previousPatchDeliveryTime = rxData?.previous_patch_delivery_time_display;
	const showPreviousPatchDeliveryTime = Boolean(rxData?.previous_patch_id && previousPatchDeliveryTime);

	const { pharmacyId } = useAuthSelector()

	const handleRadioChange = (e) => {
		if (disabled) return;
		const value = e.target.value
		setSelectedValue(value)
		const requestedData = {
			pharmacy_id: pharmacyId,
			patient_id: rxData.patient_id,
			rxnumber: rxData.rxnumber,
			is_return: value,
			patients_win_rx_data_id: rxData.id,
			setShowToast: setShowToast
		}
		dispatch(ValidateReturnedPatches(requestedData))
	};

	return (
		<React.Fragment>
			<div style={{ position: 'relative' }} className=''>


				<div style={{ border: '1px solid lightgray' }} className={`text-xs font-semibold mt-1 bg-gray-50 rounded-md px-1.5 gap-2 uppercase ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>

					<div className='text-xs font-semibold pt-2 rounded-md px-0 py-0 flex flex-col items-start gap-1 normal-case'>
						<span>Are previous patches returned?</span>
						{showPreviousPatchDeliveryTime && (
							<span className='inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 border border-blue-100'>
								{previousPatchDeliveryTime}
							</span>
						)}
					</div>

					<div className='flex items-center pt-1 pb-2 space-x-16'>
						<label className={`flex items-center cursor-pointer ${disabled ? 'cursor-not-allowed' : ''}`}>
							<input
								type='radio'
								name={`returnpatch-${rxData?.id ?? index}`}
								value='yes'
								checked={selectedValue === 'yes'}
								onChange={handleRadioChange}
								disabled={disabled}
								className='w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 cursor-pointer'
								style={{ outline: 'none', border: '0.1px solid gray', borderRadius: '4px' }}
							/>
							<span className="ml-2 pl-1 text-sm font-medium text-gray-900">YES</span>
						</label>
						<label className={`flex items-center cursor-pointer ${disabled ? 'cursor-not-allowed' : ''}`}>
							<input
								type='radio'
								name={`returnpatch-${rxData?.id ?? index}`}
								value='no'
								checked={selectedValue === 'no'}
								onChange={handleRadioChange}
								disabled={disabled}
								className='w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 cursor-pointer'
								style={{ outline: 'none', border: '0.1px solid gray', borderRadius: '4px' }}
							/>
							<span className="ml-2 pl-1 text-sm font-medium text-gray-900">NO</span>
						</label>
						<label className={`flex items-center cursor-pointer ${disabled ? 'cursor-not-allowed' : ''}`}>
							<input
								type='radio'
								name={`returnpatch-${rxData?.id ?? index}`}
								value='na'
								checked={selectedValue === 'na'}
								onChange={handleRadioChange}
								disabled={disabled}
								className='w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 cursor-pointer'
								style={{ outline: 'none', border: '0.1px solid gray', borderRadius: '4px' }}
							/>
							<span className="ml-2 pl-1 text-sm font-medium text-gray-900">NA</span>
						</label>
					</div>

				</div>

				{/* {showToast.successToast &&
					<div className='fixed  left-0 w-full bg-green-500 text-white text-center py-2 z-50'>
						<SuccessMessage message={'Updated Successfully'} />
					</div>} */}

				{showToast.errorToast &&
					<div className='fixed  left-0 w-full  text-white text-center py-2 z-50'>
						<ErrorMessage message={'Oops!.Got an Issue.please try again.'} />
					</div>}
			</div>
		</React.Fragment>
	);
};





// API sends delivey_time as a plain "YYYY-MM-DD HH:MM:SS" string (pharmacy local time).
// Parse the clock part directly so the browser timezone never shifts it.
const formatRxDeliveryTime = (raw) => {
	const match = String(raw || '').match(/(\d{1,2}):(\d{2})/);
	if (!match) return null;
	const hours = parseInt(match[1], 10);
	const period = hours >= 12 ? 'PM' : 'AM';
	const hours12 = hours % 12 || 12;
	return `${String(hours12).padStart(2, '0')}:${match[2]} ${period}`;
};

// Single-dose Rx: badge on the right of the Rx header row.
const RxDeliveredBadge = ({ time }) => (
	<span
		title={`Delivered at ${time}`}
		className="inline-flex items-center gap-1 px-3 rounded-full border border-green-200 bg-green-50 text-green-700 text-xs font-medium whitespace-nowrap"
		style={{ height: '25px' }}
	>
		<svg className="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="m5 12 4 4L19 6" />
		</svg>
		Delivered · {time}
	</span>
);

// Header badge time for a delivered single-dose Rx (no dose list), otherwise null.
const getSingleDoseDeliveredTime = (rx, doses) => {
	if (doses?.length > 0) return null;
	if (String(rx?.delivery_status || '').toLowerCase() !== 'delivered') return null;
	return formatRxDeliveryTime(rx?.delivey_time);
};

export const DoseFrequencySelector = ({
	dilaudidDelivery,
	selectedDoses = [],
	setSelectedDoses,
	previous_delivery_time_diff = null,
	minutes_until_can_deliver: minutesUntilCanDeliverFromRx = null,
	disabled = false,
	readOnly = false
}) => {

	// Normalize delivery_status for comparison (API may return "delivered" or "Delivered")
	const isDeliveredStatus = (d) => String(d?.delivery_status || '').toLowerCase() === 'delivered';

	// A selection is only still current if the dose gap prompt on it is too. The
	// prompt appears and changes as doses go out - delivering dose 1 is what gives
	// dose 2 its prompt - so comparing dose ids alone kept a copy taken before the
	// prompt existed and the driver was never asked.
	const promptKey = (prompt) => prompt
		? [prompt.requires_confirmation ? 1 : 0, prompt.can_override ? 1 : 0, prompt.next_allowed_at || ''].join('|')
		: '';
	// can_deliver logic only when status is not delivered
	const allDosesDelivered = dilaudidDelivery?.length > 0 &&
		dilaudidDelivery.every((d) => isDeliveredStatus(d));
	// Step 1: all can_deliver false → entire section read-only (only when not all doses delivered)
	const allCanDeliverFalse = !allDosesDelivered && dilaudidDelivery?.length > 0 &&
		dilaudidDelivery.every((d) => d?.can_deliver === false);

	// minutes_until_can_deliver: from RX level prop or from dose-level; API may send number or string e.g. "60 M"
	const parseMinutes = (v) => {
		if (v == null) return null;
		const n = typeof v === 'number' ? v : parseInt(String(v).trim(), 10);
		return Number.isNaN(n) ? null : n;
	};
	const endTimeRef = useRef(null);
	const [remainingSeconds, setRemainingSeconds] = useState(null);
	const minutesFromDoses = dilaudidDelivery?.length > 0
		? Math.max(0, ...(dilaudidDelivery
			.filter((d) => !isDeliveredStatus(d) && d?.minutes_until_can_deliver != null)
			.map((d) => parseMinutes(d.minutes_until_can_deliver) ?? 0)) || [0])
		: null;
	const minutesFromRx = parseMinutes(minutesUntilCanDeliverFromRx);
	const minutesFromApi = (minutesFromRx != null && minutesFromRx >= 0)
		? minutesFromRx
		: (minutesFromDoses != null && minutesFromDoses > 0 ? minutesFromDoses : null);
	useEffect(() => {
		if (minutesFromApi != null && minutesFromApi > 0) {
			endTimeRef.current = Date.now() + minutesFromApi * 60 * 1000;
			setRemainingSeconds(minutesFromApi * 60);
		} else {
			endTimeRef.current = null;
			setRemainingSeconds(null);
		}
	}, [minutesFromApi]);
	useEffect(() => {
		if (remainingSeconds == null || remainingSeconds <= 0) return;
		const interval = setInterval(() => {
			if (endTimeRef.current == null) return;
			const sec = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000));
			setRemainingSeconds(sec <= 0 ? null : sec);
		}, 1000);
		return () => clearInterval(interval);
	}, [remainingSeconds]);
	// Hide timer when 00 min 00 sec (remainingSeconds 0 or null). Format: "11m 50s"
	const timerDisplay = remainingSeconds != null && remainingSeconds > 0
		? `${Math.floor(remainingSeconds / 60)}m ${remainingSeconds % 60}s`
		: null;

	useEffect(() => {
		if (disabled || readOnly) return;
		if (!dilaudidDelivery?.length) return;
		if (allCanDeliverFalse) {
			// setSelectedDoses writes a brand new object into state shared across
			// the whole patient list, so clearing a selection that is already
			// empty still re-renders this component. This effect depends on that
			// callback, whose identity changes every render, so it would run and
			// write again - a loop that shows as a dose checkbox flickering.
			if (selectedDoses?.length) setSelectedDoses([]);
			return;
		}
		// Keep selected dose IDs matched to only doses that can be delivered now.
		// Delivered doses are shown as history only and are not submitted again.
		const fromApi = dilaudidDelivery
			.filter((d) => !isDeliveredStatus(d) && d?.can_deliver === true)
			.map((d) => ({
				frequency: d?.frequency,
				dose_id: d?.id,
				dose_no: d?.dose_no,
				dose_gap_prompt: d?.dose_gap_prompt
			}));
		const currentMatches = selectedDoses?.length === fromApi.length &&
			fromApi.every((d) => selectedDoses.some((s) => s.dose_id === d.dose_id
				&& promptKey(s.dose_gap_prompt) === promptKey(d.dose_gap_prompt)));
		if (!currentMatches) {
			setSelectedDoses(fromApi);
		}
	}, [dilaudidDelivery, selectedDoses?.length, setSelectedDoses, disabled, readOnly, allCanDeliverFalse]);

	const handleDoseToggle = (dose, index) => {
		if (disabled || readOnly) return;
		if (allCanDeliverFalse) return;
		if (isDeliveredStatus(dose)) return;
		// Selection is read-only from API can_deliver; user cannot toggle
		return;
	};

	const isDoseSelected = (dose_id) =>
		selectedDoses?.some((d) => d.dose_id === dose_id);

	// Single-dose Rxs have no dose list; their delivery shows as a header badge instead.
	if (!dilaudidDelivery || dilaudidDelivery?.length === 0) {
		return null;
	}

	const sectionReadOnly = disabled || allCanDeliverFalse;

	return (
		<div className={`mt-2 px-1 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded border border-blue-200 ${sectionReadOnly ? 'pointer-events-none opacity-60' : ''}`}>
			<div className="gap-1 flex flex-wrap">
				{dilaudidDelivery.map((delivery, index) => {
					const isSelected = isDoseSelected(delivery.id);
					const isDelivered = isDeliveredStatus(delivery);
					const isChecked = isDelivered || isSelected;
					const deliveryTimeDisplay = delivery?.delivery_time_display;
					// Selection reflects API can_deliver; all checkboxes read-only (user cannot change)
					const isDisabled = true;

					return (
						<div key={delivery.id} className="inline-flex flex-col items-start">
							<div
								onClick={() => handleDoseToggle(delivery, index)}
								className={`
									flex items-center gap-1 px-4 py-1 rounded border transition-all duration-200
									${isDisabled ? "cursor-not-allowed" : "cursor-pointer hover:border-blue-300"}
									${isDelivered ? "bg-green-50 border-green-200 text-green-700" : isSelected ? "bg-blue-100 border-blue-200 text-blue-600" : "bg-white border-gray-200 text-gray-700"}
									`}
								style={{ width: 'fit-content' }}	>
								<div
									className={`
										w-4 h-4 rounded border flex items-center justify-center
										${isChecked ? "border-white bg-white" : "border-gray-200 bg-white"}
										`}
								>
									{isChecked && (
										<svg
											className={`w-2.5 h-2.5 ${isDelivered ? "text-green-600" : "text-blue-600"}`}
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
												clipRule="evenodd"
											/>
										</svg>
									)}
								</div>
								<span
									className={`text-[12px] font-medium ${isDelivered ? "text-green-700" : isSelected ? "text-blue-600" : "text-gray-700"
										}`}
								>
									{delivery.dose} {delivery?.intervention_iv_code === 'SA' ? '(C)' : ''}
								</span>
							</div>
							{isDelivered && deliveryTimeDisplay && (
								<span className="mt-0.5 pl-1 text-xs font-medium leading-none text-green-700">
									{deliveryTimeDisplay}
								</span>
							)}
							{/* {isDoseOne && previous_delivery_time_diff && (
								<div className="text-xs text-gray-600 mt-1">
									Previous dose delivered {previous_delivery_time_diff}
								</div>
							)} */}
						</div>
					);
			})}
			</div>
			{timerDisplay ? (
				<div className="text-xs text-gray-600 mt-1">
					<div>Next delivery in {timerDisplay}</div>
				</div>
			) : null}
		</div>
	);
};


// export const DoseFrequencySelector = ({
// 	dilaudidDelivery,
// 	selectedDoses = [],
// 	setSelectedDoses,
// 	previous_delivery_time_diff = null
// }) => {

// 	useEffect(() => {
// 		if (dilaudidDelivery?.length > 0 && selectedDoses?.length === 0) {
// 			const first = dilaudidDelivery[0];

// 			// Pre-select delivered doses
// 			const deliveredDoses = dilaudidDelivery
// 				?.filter((delivery) => delivery?.delivery_status === "delivered")
// 				?.map((delivery) => ({
// 					frequency: delivery?.frequency,
// 					dose_id: delivery?.id,
// 					dose_no: delivery?.dose_no,
// 				}));

// 			// If no delivered doses, select first dose
// 			if (deliveredDoses?.length === 0) {
// 				setSelectedDoses([
// 					{ frequency: first?.frequency, dose_id: first?.id, dose_no: first?.dose_no },
// 				]);
// 			} else {
// 				setSelectedDoses(deliveredDoses);
// 			}
// 		}
// 	}, [dilaudidDelivery, selectedDoses, setSelectedDoses]);

// 	const handleDoseToggle = (dose, index) => {
// 		if (dose.delivery_status === "delivered") return; // lock delivered
// 		if (index === 0) return; // lock first dose

// 		const isAlreadySelected = selectedDoses?.some((d) => d?.dose_id === dose?.id);

// 		if (isAlreadySelected) {
// 			// Unchecking → remove this and all AFTER it
// 			const updated = selectedDoses?.filter((d) => d.dose_no < dose.dose_no);
// 			setSelectedDoses(updated);
// 		} else {
// 			// Checking → add this and all BEFORE it
// 			const upToIndex = dilaudidDelivery?.slice(0, index + 1).map((d) => ({
// 				frequency: d.frequency,
// 				dose_id: d.id,
// 				dose_no: d.dose_no,
// 			}));
// 			setSelectedDoses(upToIndex);
// 		}
// 	};

// 	const isDoseSelected = (dose_id) =>
// 		selectedDoses?.some((d) => d.dose_id === dose_id);

// 	if (!dilaudidDelivery || dilaudidDelivery.length === 0) {
// 		return null;
// 	}

// 	return (
// 		<div className="mt-2 px-1 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded border border-blue-200">
// 			<div className="gap-1 flex flex-wrap">
// 				{dilaudidDelivery.map((delivery, index) => {
// 					const isSelected = isDoseSelected(delivery.id);
// 					const isDoseOne = index === 0;
// 					const isDelivered = delivery.delivery_status === "delivered";
// 					const isDisabled = isDoseOne || isDelivered;

// 					return (
// 						<div
// 							key={delivery.id}
// 							onClick={() => handleDoseToggle(delivery, index)}
// 							className={`
// 				  flex items-center justify-between px-2 py-1 rounded border transition-all duration-200
// 				  ${isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-blue-300"}
// 				  ${isSelected ? "bg-blue-100 border-blue-200 text-blue-600" : "bg-white border-gray-200 text-gray-700"}
// 				`}
// 				style={{ width: 'fit-content' }}>
// 							<div className="flex items-center gap-1">
// 								<div
// 									className={`
// 					  w-4 h-4 rounded border flex items-center justify-center
// 					  ${isSelected ? "border-white bg-white" : "border-gray-200 bg-white"}
// 					`}
// 								>
// 									{isSelected && (
// 										<svg
// 											className="w-2.5 h-2.5 text-blue-600"
// 											fill="currentColor"
// 											viewBox="0 0 20 20"
// 										>
// 											<path
// 												fillRule="evenodd"
// 												d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
// 												clipRule="evenodd"
// 											/>
// 										</svg>
// 									)}
// 								</div>
// 								<span
// 									className={`text-xs font-medium ${isSelected ? "text-blue-600" : "text-gray-700"
// 										}`}
// 								>
// 									{delivery.dose}
// 								</span>
// 							</div>

// 							{isDoseOne && previous_delivery_time_diff && (
// 								<div className="text-xs text-gray-600 mt-1">
// 									Previous dose delivered {previous_delivery_time_diff}
// 								</div>
// 							)}
// 						</div>
// 					);
// 				})}
// 			</div>
// 		</div>
// 	);
// };
